import React from 'react';
import './Hero.css';

const getHeroClass = (selectedProduct) => {
  if (selectedProduct === 'bi') return 'hero-bi';
  if (selectedProduct === 'core') return 'hero-core';
  return 'hero-etl';
};

const Hero = ({ selectedProduct, onProductChange, onContactClick }) => {
  return (
    <section className={`hero ${getHeroClass(selectedProduct)}`}>
      <div className="hero-container">
        <div className="product-tabs-wrapper">
          <div className="product-tabs">
            <button
              className={`product-tab ${selectedProduct === 'core' ? 'active' : ''}`}
              onClick={() => onProductChange('core')}
            >
              <span className="tab-icon">🏢</span>
              <span className="tab-label">Neoshift Core</span>
            </button>
            <button
              className={`product-tab ${selectedProduct === 'etl' ? 'active' : ''}`}
              onClick={() => onProductChange('etl')}
            >
              <span className="tab-icon">⚡</span>
              <span className="tab-label">Neoshift ETL</span>
            </button>
            <button
              className={`product-tab ${selectedProduct === 'bi' ? 'active' : ''}`}
              onClick={() => onProductChange('bi')}
            >
              <span className="tab-icon">📊</span>
              <span className="tab-label">Neoshift BI</span>
            </button>
          </div>
        </div>

        <div className="hero-content" key={selectedProduct}>
          {selectedProduct === 'core' && <CoreHero onContactClick={onContactClick} />}
          {selectedProduct === 'etl' && <ETLHero onContactClick={onContactClick} />}
          {selectedProduct === 'bi' && <BIHero onContactClick={onContactClick} />}
        </div>
      </div>
    </section>
  );
};

const CoreHero = ({ onContactClick }) => (
  <>
    <div className="hero-text">
      <div className="hero-badge">Operations Platform</div>
      <h1 className="hero-title">
        From Supplier PDF to
        <span className="highlight"> Invoice — One Workspace</span>
      </h1>
      <p className="hero-subtitle">
        Neoshift Core connects master data, dynamic pricing, sales, procurement, warehouse
        operations, invoicing, and AI-assisted document processing — so your team stops
        re-keying and starts approving.
      </p>
      <div className="hero-actions">
        <a
          href="https://core.neoshift.ai"
          className="hero-cta primary"
          target="_blank"
          rel="noopener noreferrer"
        >
          Launch Neoshift Core
        </a>
        <button className="hero-cta secondary" onClick={onContactClick}>Book a Demo</button>
      </div>
      <div className="hero-stats">
        <div className="stat">
          <span className="stat-number">AI</span>
          <span className="stat-label">Document Intake</span>
        </div>
        <div className="stat">
          <span className="stat-number">WMS</span>
          <span className="stat-label">+ Floor PWA</span>
        </div>
        <div className="stat">
          <span className="stat-number">Network</span>
          <span className="stat-label">Partner Sync</span>
        </div>
      </div>
    </div>
    <div className="hero-visual">
      <div className="hero-image">
        <div className="floating-card card-1">
          <div className="card-icon">📄</div>
          <div className="card-text">AI Document Intake</div>
        </div>
        <div className="floating-card card-2">
          <div className="card-icon">💰</div>
          <div className="card-text">Dynamic Pricing</div>
        </div>
        <div className="floating-card card-3">
          <div className="card-icon">📦</div>
          <div className="card-text">Warehouse PWA</div>
        </div>
        <div className="main-visual">
          <div className="data-flow">
            <div className="input-data">PO &amp; SO</div>
            <div className="arrow">→</div>
            <div className="process-data">Fulfill</div>
            <div className="arrow">→</div>
            <div className="output-data">Invoice</div>
          </div>
        </div>
      </div>
    </div>
  </>
);

const ETLHero = ({ onContactClick }) => (
  <>
    <div className="hero-text">
      <h1 className="hero-title">
        AI That Automates Your
        <span className="highlight"> Business Operations</span>
      </h1>
      <p className="hero-subtitle">
        Turn painful manual processes into seamless workflows — automatically.
        Our AI extracts, validates, and syncs data from any document into your
        existing systems, replacing hours of work with seconds.
      </p>
      <div className="hero-actions">
        <button className="hero-cta primary" onClick={onContactClick}>Get a Free POC</button>
        <button className="hero-cta secondary" onClick={onContactClick}>Book a Demo</button>
      </div>
      <div className="hero-stats">
        <div className="stat">
          <span className="stat-number">80%</span>
          <span className="stat-label">Less Manual Work</span>
        </div>
        <div className="stat">
          <span className="stat-number">4 Wks</span>
          <span className="stat-label">To Go-Live</span>
        </div>
        <div className="stat">
          <span className="stat-number">&lt;1%</span>
          <span className="stat-label">Error Rate</span>
        </div>
      </div>
    </div>
    <div className="hero-visual">
      <div className="hero-image">
        <div className="floating-card card-1">
          <div className="card-icon">📄</div>
          <div className="card-text">PO &amp; Invoice Processing</div>
        </div>
        <div className="floating-card card-2">
          <div className="card-icon">🔄</div>
          <div className="card-text">Multi-System Sync</div>
        </div>
        <div className="floating-card card-3">
          <div className="card-icon">✅</div>
          <div className="card-text">AI Validation</div>
        </div>
        <div className="main-visual">
          <div className="data-flow">
            <div className="input-data">Read &amp; Extract</div>
            <div className="arrow">→</div>
            <div className="process-data">Validate</div>
            <div className="arrow">→</div>
            <div className="output-data">Process &amp; Sync</div>
          </div>
        </div>
      </div>
    </div>
  </>
);

const BIHero = ({ onContactClick }) => (
  <>
    <div className="hero-text">
      <div className="hero-badge">AI-Powered Platform</div>
      <h1 className="hero-title">
        Ask Questions, Get
        <span className="highlight"> Instant Insights</span>
      </h1>
      <p className="hero-subtitle">
        Neoshift BI lets you analyze data, build interactive dashboards, and uncover
        insights using natural language. No SQL or coding required — just ask and your
        AI analyst delivers.
      </p>
      <div className="hero-actions">
        <a
          href="https://bi.neoshift.ai"
          className="hero-cta primary"
          target="_blank"
          rel="noopener noreferrer"
        >
          Launch Neoshift BI
        </a>
        <button className="hero-cta secondary" onClick={onContactClick}>Request Demo</button>
      </div>
      <div className="hero-stats">
        <div className="stat">
          <span className="stat-number">AI</span>
          <span className="stat-label">Powered Analytics</span>
        </div>
        <div className="stat">
          <span className="stat-number">6+</span>
          <span className="stat-label">Data Sources</span>
        </div>
        <div className="stat">
          <span className="stat-number">0</span>
          <span className="stat-label">SQL Required</span>
        </div>
      </div>
    </div>
    <div className="hero-visual">
      <div className="hero-video">
        <iframe
          src="https://www.youtube.com/embed/0Y_e38oUmW8"
          title="Neoshift BI Demo"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
        ></iframe>
      </div>
    </div>
  </>
);

export default Hero;
